import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import TechniqueCards from "@/components/TechniqueCards";
import FeatureCard from "@/components/FeatureCard";
import LandingCTA from "@/components/LandingCTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Building2, Check, CheckCircle2, Heart, Palette, PartyPopper, Shirt, Users } from "@/components/Icon";
import heroImage from "@/assets/hero-printing.webp";
import printDetailImage from "@/assets/dtf/dtf-transfer-detail.webp";

const SITE_URL = "https://www.magicprint.nl";
const PAGE_URL = `${SITE_URL}/t-shirt-bedrukken`;
const TITLE = "T-shirts bedrukken met eigen logo vanaf 10 stuks | Magic Print";
const DESCRIPTION = "T-shirts laten bedrukken met je eigen logo of ontwerp? Kies uit verschillende modellen en kleuren. Vanaf 10 stuks. Vraag een offerte aan bij Magic Print.";
const OG_IMAGE = `${SITE_URL}${heroImage}`;

const faqs = [
  { q: "Vanaf hoeveel T-shirts kan ik bestellen?", a: "Bij Magic Print kun je T-shirts laten bedrukken vanaf 10 stuks." },
  { q: "Kan ik verschillende maten bestellen?", a: "Ja. Binnen een bestelling kunnen verschillende maten worden gecombineerd. Welke maten beschikbaar zijn, hangt af van het gekozen T-shirt." },
  { q: "Kan ik mijn eigen logo aanleveren?", a: "Ja. Je kunt je eigen logo, tekst of ontwerp aanleveren. Als het bestand niet geschikt is voor productie, kunnen we aangeven wat er nodig is." },
  { q: "Welke druktechniek is het beste voor mijn T-shirts?", a: "Dat hangt af van het ontwerp, het aantal shirts en het gekozen textiel. Voor grotere oplages kan zeefdruk interessant zijn, terwijl DTF geschikt kan zijn voor ontwerpen met veel kleuren en details. We adviseren dit bij de aanvraag." },
  { q: "Kan de voor- én achterkant worden bedrukt?", a: "Ja. Afhankelijk van het ontwerp kunnen bijvoorbeeld borst, voorkant en rug worden bedrukt. Vermeld de gewenste posities bij je offerteaanvraag." },
  { q: "Kan ik eerst de T-shirts op de website bekijken?", a: "Ja. In de productcatalogus kun je beschikbare T-shirts, modellen en kleuren bekijken. Vanuit een product kun je vervolgens verder naar een offerteaanvraag." },
];

const steps = [
  { title: "Kies je T-shirt", text: "Kies een model uit ons assortiment of vertel ons waarvoor je de shirts nodig hebt." },
  { title: "Geef maten en aantallen door", text: "Verschillende maten kunnen binnen dezelfde bestelling worden gecombineerd." },
  { title: "Stuur je logo of ontwerp", text: "Geef ook aan waar je de bedrukking wilt hebben, bijvoorbeeld borst, voorkant of rug." },
  { title: "Je ontvangt een offerte", text: "We bekijken het textiel, de oplage en het ontwerp en bepalen welke productiemethode daarbij past." },
  { title: "Na akkoord gaan we aan de slag", text: "Na akkoord op de bestelling kan de productie worden ingepland." },
];

const shirtFeatures = [
  { icon: Shirt, title: "Voor dagelijks gebruik", description: "Comfortabele T-shirts voor personeel en bedrijfskleding die regelmatig worden gedragen." },
  { icon: PartyPopper, title: "Promotie & evenementen", description: "Geschikt voor acties, evenementen en bestellingen waarbij meerdere shirts nodig zijn." },
  { icon: Heart, title: "Organic T-shirts", description: "Keuze uit modellen van biologisch katoen voor wie bewust textiel wil kiezen." },
  { icon: Users, title: "Verschillende pasvormen", description: "Afhankelijk van het assortiment zijn heren-, dames- en uniseksmodellen beschikbaar." },
  { icon: Palette, title: "Veel kleuren", description: "Kies afhankelijk van het model uit verschillende kleuren die bij je huisstijl of ontwerp passen." },
  { icon: CheckCircle2, title: "Vanaf 10 stuks", description: "Combineer verschillende maten binnen dezelfde bestelling." },
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "T-shirts bedrukken", item: PAGE_URL },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    })),
  },
];

const TShirtBedrukken = () => (
  <>
    <Helmet>
      <title>{TITLE}</title>
      <meta name="description" content={DESCRIPTION} />
      <link rel="canonical" href={PAGE_URL} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Magic Print" />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:title" content={TITLE} />
      <meta property="og:description" content={DESCRIPTION} />
      <meta property="og:url" content={PAGE_URL} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={TITLE} />
      <meta name="twitter:description" content={DESCRIPTION} />
      <meta name="twitter:image" content={OG_IMAGE} />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
    <Navbar />
    <main className="min-h-screen overflow-x-clip bg-background">
      <section className="relative flex min-h-[680px] items-center overflow-hidden pt-28 md:min-h-[720px] md:pt-36">
        <img src={heroImage} alt="Bedrukte T-shirts in de productie van Magic Print" className="absolute inset-0 h-full w-full object-cover" width={1920} height={1080} />
        <div className="absolute inset-0 bg-surface-dark/70" />
        <div className="container relative z-10 py-14 text-surface-dark-foreground md:py-20">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">T-shirts bedrukken met je eigen logo of ontwerp</h1>
            <div className="mt-6 max-w-2xl space-y-4 text-base leading-relaxed text-surface-dark-foreground/90 md:text-lg">
              <p>T-shirts laten bedrukken voor je bedrijf, vereniging, evenement of team? Magic Print bedrukt T-shirts vanaf 10 stuks met je eigen logo, tekst of ontwerp.</p>
              <p>We helpen je niet alleen met de bedrukking. Je kunt ook bij ons terecht voor de T-shirts zelf. Zo hoef je niet eerst ergens kleding te bestellen en daarna een drukker te zoeken. Je kiest het model, de kleur en de maten, levert je ontwerp aan en wij zorgen voor de rest.</p>
            </div>
            <ul className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-7">
              {["Vanaf 10 stuks", "Bedrukt met je eigen logo", "Persoonlijk advies"].map((usp) => (
                <li key={usp} className="flex items-center gap-2 font-semibold"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary"><Check className="h-3 w-3" /></span>{usp}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" asChild><Link to="/offerte">Vraag een offerte aan <ArrowRight /></Link></Button>
              <Button size="lg" variant="outline" className="text-foreground" asChild><Link to="/producten?cat=tshirt">Bekijk de T-shirts</Link></Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">Welk T-shirt past bij jouw bestelling?</h2>
            <div className="mt-4 space-y-3 leading-relaxed text-muted-foreground">
              <p>Een T-shirt voor personeel stelt andere eisen dan een shirt dat één dag tijdens een evenement wordt gedragen. Daarom werken we niet met één standaardshirt voor iedere aanvraag.</p>
              <p>Voor dagelijks gebruik zijn pasvorm, draagcomfort en wasbestendigheid belangrijk. Voor een evenement kan juist de prijs bij een grotere oplage doorslaggevend zijn. En wie bewust voor textiel wil kiezen, kan kijken naar onze organic modellen.</p>
              <p>In ons assortiment vind je T-shirts voor onder andere bedrijven, personeel, verenigingen, teams, promotionele acties, evenementen en werkkleding.</p>
            </div>
          </div>
          <div className="mt-9 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {shirtFeatures.map((feature) => <FeatureCard key={feature.title} {...feature} />)}
          </div>
          <div className="mt-7 flex justify-center">
            <Button asChild><Link to="/producten?cat=tshirt">Bekijk ons assortiment T-shirts <ArrowRight /></Link></Button>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-12 md:py-16">
        <div className="container grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
          <div className="overflow-hidden rounded-2xl">
            <img src={printDetailImage} alt="Detail van een bedrukking op textiel bij Magic Print" loading="lazy" width={800} height={600} className="aspect-[4/3] h-full w-full object-cover" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">Je logo of ontwerp op een T-shirt</h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-muted-foreground">
              <p>Een klein logo op de borst, een grote print op de rug of een ontwerp over de voorkant: de juiste bedrukking hangt af van wat je wilt laten maken.</p>
              <p>Daarbij kijken we naar drie dingen: het ontwerp, het materiaal van het shirt en het aantal stuks. Op basis daarvan bepalen we welke druktechniek geschikt is.</p>
              <p>Je hoeft dus niet zelf uit te zoeken welke techniek je moet bestellen. Stuur ons je ontwerp en vertel waarvoor je de shirts wilt gebruiken. Dan kunnen we adviseren wat logisch is.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container">
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">Welke druktechniek past bij jouw T-shirt?</h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">Niet ieder ontwerp vraagt om dezelfde druktechniek. Het aantal shirts, het aantal kleuren en de details in je ontwerp bepalen welke methode het meest geschikt is. Je hoeft dat niet zelf uit te zoeken — wij adviseren je bij je aanvraag.</p>
          </div>
          <TechniqueCards items={["zeefdruk", "dtf", "transferdruk"]} />
          <p className="mx-auto mt-8 max-w-3xl text-center text-muted-foreground">Niet zeker welke techniek je nodig hebt? Dat hoef je vooraf niet te weten. Stuur je logo of ontwerp mee met je aanvraag; wij kijken welke methode bij de bestelling past.</p>
        </div>
      </section>

      <section className="bg-secondary py-12 md:py-16">
        <div className="container grid gap-6 lg:grid-cols-2">
          <article className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 sm:p-8">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10"><Building2 className="h-6 w-6 text-primary" /></div>
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">T-shirts voor bedrijven en teams</h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-muted-foreground">
              <p>Voor bedrijfskleding draait het niet alleen om een logo. Als medewerkers het shirt regelmatig dragen, moet het ook prettig zitten en passen bij de uitstraling van het bedrijf.</p>
              <p>Je kunt verschillende maten binnen dezelfde bestelling combineren. Afhankelijk van het gekozen model zijn meerdere kleuren beschikbaar, zodat de kleding kan aansluiten op je huisstijl.</p>
              <p>Wil je naast T-shirts ook sweaters, jassen of andere kleding laten bedrukken? Dan kunnen we de kleding voor het hele team op elkaar afstemmen.</p>
            </div>
            <div className="mt-auto pt-7"><Button variant="outline" asChild><Link to="/bedrijfskleding-werkkleding">Bekijk bedrijfskleding <ArrowRight /></Link></Button></div>
          </article>
          <article className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 sm:p-8">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10"><PartyPopper className="h-6 w-6 text-primary" /></div>
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">T-shirts voor verenigingen en evenementen</h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-muted-foreground">
              <p>Voor een vereniging, studententeam of evenement kan één ontwerp in meerdere maten nodig zijn. Ook daarvoor kun je vanaf 10 stuks bij Magic Print terecht.</p>
              <p>Heb je al een ontwerp? Stuur het direct mee. Heb je alleen een logo of een idee, dan kunnen we eerst bekijken wat er technisch nodig is om het goed op het shirt te krijgen.</p>
              <p>Bij grotere aantallen maken we een offerte op basis van het gekozen shirt, het aantal, de bedrukking en het aantal posities.</p>
            </div>
          </article>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container">
          <div className="mx-auto mb-9 max-w-3xl text-center"><h2 className="text-3xl font-bold text-foreground md:text-4xl">Zo werkt T-shirts laten bedrukken</h2></div>
          <ol className="relative grid gap-5 md:grid-cols-2 lg:grid-cols-5 lg:gap-4 before:absolute before:left-[10%] before:right-[10%] before:top-[1.375rem] before:hidden before:h-px before:bg-border lg:before:block">
            {steps.map((step, index) => (
              <li key={step.title} className="relative flex h-full flex-col rounded-lg border border-border bg-card p-5 lg:border-0 lg:bg-background lg:px-3 lg:pb-0 lg:pt-0 lg:text-center">
                <span className="relative z-10 mb-4 flex h-11 w-11 items-center justify-center rounded-full border-2 border-primary bg-background font-bold text-primary lg:mx-auto">{index + 1}</span>
                <h3 className="font-bold text-foreground">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-surface-dark py-12 text-surface-dark-foreground md:py-16">
        <div className="container grid items-center gap-10 lg:grid-cols-[1fr_0.8fr] lg:gap-16">
          <div>
            <h2 className="text-3xl font-bold md:text-4xl">T-shirts bedrukken vanaf 10 stuks</h2>
            <p className="mt-5 text-lg leading-relaxed text-surface-dark-foreground/80">Magic Print werkt met een minimale afname van 10 stuks. Dat maakt deze dienst vooral geschikt voor bedrijven, teams, verenigingen, evenementen en andere bestellingen waarbij meerdere shirts met hetzelfde ontwerp nodig zijn.</p>
            <p className="mt-4 text-lg text-surface-dark-foreground/80">Daarom maken we hiervoor een offerte op maat.</p>
            <Button className="mt-7" size="lg" asChild><Link to="/offerte">Vraag vrijblijvend een offerte aan <ArrowRight /></Link></Button>
          </div>
          <div>
            <p className="mb-4 font-bold">De prijs per shirt wordt onder andere bepaald door:</p>
            <ul className="grid gap-3 sm:grid-cols-2">
              {["het gekozen T-shirt", "het aantal shirts", "de druktechniek", "het formaat van de bedrukking", "het aantal te bedrukken posities", "het ontwerp"].map((item) => (
                <li key={item} className="flex items-start gap-2 text-surface-dark-foreground/85"><Check className="mt-1 h-4 w-4 shrink-0 text-primary" />{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-12 md:py-16" id="veelgestelde-vragen">
        <div className="container max-w-3xl">
          <h2 className="mb-10 text-center text-3xl font-bold text-foreground md:text-4xl">Veelgestelde vragen over T-shirts bedrukken</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.q} value={`tshirt-faq-${index}`} className="rounded-lg border border-border bg-card px-6 data-[state=open]:border-primary/40">
                <AccordionTrigger className="text-left font-medium hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="leading-relaxed text-muted-foreground">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container max-w-4xl">
          <LandingCTA
            title="Klaar om je T-shirts te laten bedrukken?"
            description="Vertel ons welke T-shirts je nodig hebt, hoeveel stuks je wilt bestellen en stuur je logo of ontwerp mee. Dan kunnen we gericht bekijken welke combinatie van textiel en bedrukking geschikt is."
            primaryLabel="Vraag een offerte aan"
            primaryTo="/offerte"
            secondaryLabel="Bekijk de T-shirts"
            secondaryTo="/producten?cat=tshirt"
          />
        </div>
      </section>
    </main>
    <Footer />
    <WhatsAppButton />
  </>
);

export default TShirtBedrukken;
